import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useSessionTimeout } from "@/hooks/useSessionTimeout";
import { useAuthStore } from "@/hooks/useAuthStore";
import { useNavigate } from "react-router-dom";

export function SessionTimeoutWarning() {
  const { t } = useLanguage();
  const { showWarning, resetTimeout } = useSessionTimeout();
  const [countdown, setCountdown] = useState(60);
  const navigate = useNavigate();
  const auth = useAuthStore();

  useEffect(() => {
    let interval: number | undefined;

    if (showWarning) {
      setCountdown(60); // Reset to 60 seconds
      interval = window.setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      setCountdown(60);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [showWarning]);

  const handleContinue = () => {
    resetTimeout();
  };

  const handleLogout = async () => {
    try {
      await auth.logout();
      navigate("/login");
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error("Error logging out:", error);
    }
  };

  return (
    <AlertDialog open={showWarning}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t.session.timeoutWarningTitle}</AlertDialogTitle>
          <AlertDialogDescription>
            {t.session.timeoutWarningMessage}
            <div className="mt-2 font-medium">
              {t.session.timeoutCountdown.replace("{seconds}", countdown.toString())}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel asChild onClick={handleLogout}>
            <Button variant="outline">{t.session.logoutNow}</Button>
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleContinue}>{t.session.stayLoggedIn}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
